import React from 'react';
import PropTypes from 'prop-types';
import { has, isEmpty } from 'lodash';
import { css } from '@emotion/core';
import { useDispatch } from 'react-redux';
import { vunit } from '../lib/style-helpers';
import groupType from '../proptypes/group';
import phrasesType from '../proptypes/phrases';
import profileWithArchetypeType from '../proptypes/profile-with-archetype';
import selectVisitDate from '../selectors/visit-date';
import AccountLayout from '../layouts/AccountLayout';
import PageSection from '../components/PageSection';
import ComparisonPicker from '../components/ComparisonPicker';
import { Header3, BoldParagraph } from '../components/Typography';
import FallbackModule from '../components/FallbackModule';
import ComparisonTable from '../components/ComparisonTable';
import InviteFriends from '../components/invite-friends/InviteFriends';
import {
  clickComparisonInviteButtonAction,
  copyComparisonInviteUrlAction,
  shareComparisonInviteUrlAction,
} from '../actions/action-creators';

const groupUserHeaderCss = css`
  padding-top: ${vunit(8)};
  padding-bottom: ${vunit(8)};
`;

const inviteFriendsCSS = css`
  margin-top: 2rem;
`;

const GroupComparisonPage = ({
  group,
  currentProfile,
  comparedProfile,
  phrases,
  urls,
}) => {
  const {
    user: { noFullNameAvailableFallback },
    comparison: {
      pageTitle,
      visitDatePrefix,
      noDataInstruction,
      noDataMessage,
    },
  } = phrases;

  const comparisonTableDataAvailable = has(currentProfile, 'archetype.name') && has(comparedProfile, 'archetype.name');
  const comparisonPickerDataAvailable = !isEmpty(group);

  const dispatch = useDispatch();

  const handleInviteButtonClick = () => {
    dispatch(clickComparisonInviteButtonAction({
      profileId: currentProfile.profile.id,
    }));
  };

  const handleCopy = () => {
    dispatch(copyComparisonInviteUrlAction({
      profileId: currentProfile.profile.id,
    }));
  };

  const handleInviteShare = ({ id: socialNetwork }) => {
    dispatch(shareComparisonInviteUrlAction({
      socialNetwork,
      profileId: currentProfile.profile.id,
    }));
  };

  return (
    <AccountLayout>
      {comparisonPickerDataAvailable && (
      <PageSection theme="grey" className="comparison-picker-section" css={groupUserHeaderCss}>
        <Header3>{pageTitle}</Header3>
        <ComparisonPicker
          group={group}
          profileToCompareId={comparedProfile.profile.id}
          noFullNameAvailableFallback={noFullNameAvailableFallback}
        />
        <BoldParagraph>{`${visitDatePrefix} ${selectVisitDate(group[0])}`}</BoldParagraph>
        <InviteFriends
          css={inviteFriendsCSS}
          referralUrl={urls.referralUrl}
          phrases={phrases}
          onMainButtonClick={handleInviteButtonClick}
          onCopy={handleCopy}
          onShare={handleInviteShare}
        />
      </PageSection>
      )}
      {comparisonTableDataAvailable ? (
        <ComparisonTable
          currentProfile={currentProfile}
          comparedProfile={comparedProfile}
          phrases={phrases}
          urls={urls}
        />
      )
        : (
          <FallbackModule
            heading={pageTitle}
            message={noDataMessage}
            buttonCopy={noDataInstruction}
            themeClassName="module-gray"
          />
        )
      }
    </AccountLayout>
  );
};

GroupComparisonPage.defaultProps = {
  currentProfile: null,
  comparedProfile: null,
  group: [],
};

GroupComparisonPage.propTypes = {
  currentProfile: profileWithArchetypeType,
  comparedProfile: profileWithArchetypeType,
  group: groupType,
  phrases: phrasesType.isRequired,
  urls: PropTypes.shape({
    gamesPath: PropTypes.string.isRequired,
    currentProfileSpyRolePath: PropTypes.string.isRequired,
    specialOpsVideoPath: PropTypes.string.isRequired,
    referralUrl: PropTypes.string.isRequired,
  }).isRequired,
};

export default GroupComparisonPage;
